import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Snackbar,
    TextField,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { collection, doc, getDoc, getDocs, onSnapshot, setDoc } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { db } from '../../firebase.js';
import { useUser } from '../../contexts/UserContext.js';
import { isDangerousWasteDeploymentUnit } from '../../common/deploymentCategory.js';
import {
    getActiveUnitIdsForDisplayUnit,
    getCurrentWritableUnitId,
} from '../../common/displayUnitRanges.js';
import {
    // LEGACY_SORTING_ITEMS: These existing table helpers remain until import/export migration tickets.
    createSortingListTsvImportDraft,
    parseSortingListTsvRowsWithMappings,
    serializeSortingListTsv,
} from '../../common/dangerousWasteSortingList.js';
import {
    canUserManageWasteReceiverSortingList,
    filterSortableWasteReceivers,
    WASTE_RECEIVERS_COLLECTION,
} from '../../common/wasteReceivers.js';
import {
    buildSortingListWritePayload,
    createSortingTableColumns,
    legacyRowsToTableRows,
    normalizeLegacyTableRows,
    normalizeColumnReferencesToNames,
    normalizeDanishComparable,
    normalizeTransitionWasteReceiverDoc,
    positionalRowsToTableRows,
    selectSortingListMode,
    SORTING_LIST_MODES,
    summarizeLegacyRowMapping,
    tableRowsToPositionalList,
    validatePositionalTableRows,
    validateTransitionColumns,
} from '../../common/wasteReceiverSortingListTransition.js';
import {
    buildDualFormatSortingListImportPayload,
    convertLegacySortingItemsToPositionalRows,
    resolveSortingListAddOrUpdateImport,
} from '../../common/wasteReceiverSortingListMigrations.js';
import { describeFirestoreError } from '../../common/firestoreErrors.js';
import './DangerousWasteSortingListPage.css';

const SORTING_COLUMN_WIDTHS = {
    constituent: 'minmax(240px, 1.8fr)',
    wasteGroup: '140px',
    waste_group: '140px',
    unNumber: '128px',
    un_number: '128px',
    hazardClasses: '150px',
    hazard_classes: '150px',
    declaration: 'minmax(220px, 1.6fr)',
    instruction: 'minmax(220px, 1.6fr)',
};

function buildSortingColumns(columns, noShowColumns = []) {
    const normalized = createSortingTableColumns(columns, SORTING_COLUMN_WIDTHS);
    const hiddenColumnNames = new Set(
        normalizeColumnReferencesToNames(noShowColumns, columns).map(normalizeDanishComparable),
    );

    return normalized.map((column) => ({
        ...column,
        isHidden: hiddenColumnNames.has(normalizeDanishComparable(column.display_name)),
    }));
}

const makeRowId = (prefix) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptySortingRow = (columns = []) => {
    const row = { id: makeRowId('sorting') };
    columns.forEach((column) => {
        row[column.field] = '';
    });
    return row;
};

const TABLE_INPUT_MIN_HEIGHT_PX = 58;
const IMPORT_DUPLICATE_INPUT_MIN_HEIGHT_PX = 32;

const AutoSizeTextarea = ({ value, onChange, minHeightPx = TABLE_INPUT_MIN_HEIGHT_PX, ...props }) => {
    const textareaRef = useRef(null);

    const resize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        // Reset first so the textarea can shrink when text is removed.
        // Using 'auto' avoids occasional scrollHeight under-reporting in some browsers.
        el.style.height = 'auto';
        // Add a small buffer to avoid 1px clipping due to subpixel rounding.
        const nextHeight = Math.max((el.scrollHeight || 0) + 2, minHeightPx);
        el.style.height = `${nextHeight}px`;
    }, [minHeightPx]);

    useLayoutEffect(() => {
        resize();
    }, [resize, value]);

    return (
        <textarea
            {...props}
            ref={textareaRef}
            value={value}
            onChange={(event) => {
                onChange?.(event);
                // Resize immediately for responsiveness while typing/pasting.
                resize();
            }}
        />
    );
};

function getDisplayUnitLabelUnitId(displayUnit) {
    return (
        getActiveUnitIdsForDisplayUnit(displayUnit)[0] ||
        displayUnit?.unit_id ||
        displayUnit?.unit_ranges?.find((range) => range?.unit_id)?.unit_id ||
        null
    );
}

function normalizeRowsForSnapshot(mode, sortingItems, columns) {
    if (mode === SORTING_LIST_MODES.POSITIONAL) {
        return tableRowsToPositionalList(sortingItems, columns);
    }

    // LEGACY_SORTING_ITEMS: Keep legacy dirty checking aligned with compatibility normalization.
    return normalizeLegacyTableRows(sortingItems, columns);
}

function snapshotFor(mode, version, sortingItems, columns) {
    return JSON.stringify({
        mode,
        version: String(version || '').trim(),
        sortingItems: normalizeRowsForSnapshot(mode, sortingItems, columns),
    });
}

function snapshotForCancel(mode, version, sortingItems, columns) {
    if (mode === SORTING_LIST_MODES.POSITIONAL) {
        return JSON.stringify({
            mode,
            version: String(version || '').trim(),
            sortingItems: tableRowsToPositionalList(sortingItems, columns),
        });
    }

    return JSON.stringify({
        mode,
        version: String(version || '').trim(),
        // LEGACY_SORTING_ITEMS: Preserve draft-only legacy rows while checking cancellation.
        // Keep blank rows (and their ids) so "Annuller" confirms even if the user
        // only added a new empty row or similar draft-only edits.
        sortingItems: normalizeLegacyTableRows(sortingItems, columns, { keepBlank: true }),
    });
}

function getSafeFileNamePart(value, fallback = 'sorteringsliste') {
    return String(value || fallback)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9æøå]+/gi, '-')
        .replace(/^-+|-+$/g, '') || fallback;
}

function downloadTextFile(filename, text, type) {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function fetchCompanyLabel(companyId) {
    if (!companyId) return '';
    try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (!snap.exists()) return companyId;
        const data = snap.data();
        return data.name || data.display_name || data.label || companyId;
    } catch (error) {
        console.error('Error loading company label:', error);
        return companyId;
    }
}

async function loadAccessibleCompanyLocations(userData) {
    const locationsByKey = new Map();

    const addLocation = (companyId, locationId, data = {}) => {
        if (!companyId || !locationId) return;
        locationsByKey.set(`${companyId}/${locationId}`, {
            ...data,
            id: data.id || locationId,
            company: data.company || companyId,
        });
    };

    if (userData?.isAdmin) {
        const companiesSnap = await getDocs(collection(db, 'companies'));
        await Promise.all(
            companiesSnap.docs.map(async (companyDoc) => {
                const companyId = companyDoc.id;
                const snap = await getDocs(collection(db, `companies/${companyId}/locations`));
                snap.forEach((locationDoc) => {
                    addLocation(companyId, locationDoc.id, locationDoc.data());
                });
            }),
        );

        return Array.from(locationsByKey.values());
    }

    const locationCompanies = userData?.location_companies || {};
    await Promise.all(
        Object.values(locationCompanies).map(async (entry) => {
            const companyId = entry?.company;
            const locationId = entry?.location;
            if (!companyId || !locationId) return;
            const snap = await getDoc(doc(db, `companies/${companyId}/locations`, locationId));
            if (snap.exists()) addLocation(companyId, snap.id, snap.data());
        }),
    );

    await Promise.all(
        (userData?.companies || []).map(async (companyId) => {
            const snap = await getDocs(collection(db, `companies/${companyId}/locations`));
            snap.forEach((locationDoc) => {
                addLocation(companyId, locationDoc.id, locationDoc.data());
            });
        }),
    );

    return Array.from(locationsByKey.values());
}

async function loadAccessibleDangerousWasteCompanies(userData) {
    const locations = await loadAccessibleCompanyLocations(userData);
    const companies = new Map();

    const ensureCompany = (location, displayName) => {
        const companyId = location.company;
        if (!companyId) return null;
        if (!companies.has(companyId)) {
            companies.set(companyId, {
                companyId,
                label: companyId,
                locations: new Set(),
                unitIds: new Set(),
                machineCount: 0,
                exampleMachineName: '',
            });
        }
        const company = companies.get(companyId);
        company.locations.add(location.name || location.id || '');
        company.machineCount += 1;
        if (!company.exampleMachineName && displayName) {
            company.exampleMachineName = displayName;
        }
        return company;
    };

    locations.forEach((location) => {
        if (!Array.isArray(location.display_units)) return;
        location.display_units.forEach((displayUnit) => {
            if (!isDangerousWasteDeploymentUnit(displayUnit)) return;
            const unitId = getDisplayUnitLabelUnitId(displayUnit);
            const company = ensureCompany(
                location,
                displayUnit.display_name || displayUnit.description || unitId,
            );
            const currentUnitId = getCurrentWritableUnitId(displayUnit) || unitId;
            if (company && currentUnitId) company.unitIds.add(currentUnitId);
        });
    });

    const withLabels = await Promise.all(
        Array.from(companies.values()).map(async (company) => ({
            ...company,
            label: await fetchCompanyLabel(company.companyId),
            locations: Array.from(company.locations).filter(Boolean).sort(),
            unitIds: Array.from(company.unitIds).filter(Boolean).sort(),
        })),
    );

    return withLabels.sort((a, b) => {
        return a.label.localeCompare(b.label);
    });
}

const DangerousWasteSortingListPage = () => {
    const { t } = useTranslation();
    const { userData } = useUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedWasteReceiverId = searchParams.get('receiver');

    const sortingFileInputRef = useRef(null);
    const sortingTableWrapRef = useRef(null);
    const pendingScrollToNewRowRef = useRef(false);

    const [companyContexts, setCompanyContexts] = useState([]);
    const [companyLabelsById, setCompanyLabelsById] = useState({});
    const [wasteReceivers, setWasteReceivers] = useState([]);
    const [selectedWasteReceiverId, setSelectedWasteReceiverId] = useState('');
	const [sortingListMode, setSortingListMode] = useState(SORTING_LIST_MODES.POSITIONAL);
	const [sortingListVersion, setSortingListVersion] = useState('');
	const [savedSortingListVersion, setSavedSortingListVersion] = useState('');
	const [sortingItems, setSortingItems] = useState([]);
	const [savedSortingItems, setSavedSortingItems] = useState([]);
	const [savedSnapshot, setSavedSnapshot] = useState('');
	const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingWasteReceivers, setLoadingWasteReceivers] = useState(true);
    const [saving, setSaving] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [versionDialogOpen, setVersionDialogOpen] = useState(false);
	const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [pendingDeleteRowId, setPendingDeleteRowId] = useState(null);
	const [importMappingDialogOpen, setImportMappingDialogOpen] = useState(false);
	const [pendingImportHeaders, setPendingImportHeaders] = useState([]);
	const [pendingImportRows, setPendingImportRows] = useState([]);
	const [importMappings, setImportMappings] = useState([]);
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [pendingImportContext, setPendingImportContext] = useState(null);
	const [pendingImportPositionalRows, setPendingImportPositionalRows] = useState([]);
	const [importDuplicateReviewRowNumbers, setImportDuplicateReviewRowNumbers] = useState([]);
	const [pendingDualWriteRows, setPendingDualWriteRows] = useState(null);
	const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
	const [cancelEditDialogOpen, setCancelEditDialogOpen] = useState(false);
	const [snackbar, setSnackbar] = useState(null);

    const accessibleWasteReceivers = useMemo(
        () => filterSortableWasteReceivers(userData, wasteReceivers),
        [userData, wasteReceivers],
    );
    const selectedWasteReceiver = useMemo(
        () =>
            accessibleWasteReceivers.find((receiver) => receiver.id === selectedWasteReceiverId) ||
            null,
        [accessibleWasteReceivers, selectedWasteReceiverId],
    );
    const canEditSelectedSortingList = useMemo(
        () => canUserManageWasteReceiverSortingList(userData, selectedWasteReceiver),
        [userData, selectedWasteReceiver],
    );
    const selectedCompanyIds = useMemo(
        () => selectedWasteReceiver?.companies || [],
        [selectedWasteReceiver],
    );
    const sortingColumns = useMemo(
        () => buildSortingColumns(
            selectedWasteReceiver?.columns || [],
            selectedWasteReceiver?.noShowColumns || [],
        ),
        [selectedWasteReceiver],
    );
    const allSortingColumns = useMemo(
        () => sortingColumns.map(({ isHidden, ...column }) => column),
        [sortingColumns],
    );
    const hasSortingColumns = sortingColumns.length > 0;
    const selectedCompanyLabels = useMemo(
        () => {
            const labelByCompanyId = new Map(
                companyContexts.map((company) => [company.companyId, company.label]),
            );
            return selectedCompanyIds
                .map((companyId) => (
                    companyLabelsById[companyId] ||
                    labelByCompanyId.get(companyId) ||
                    companyId
                ))
                .sort((a, b) => a.localeCompare(b));
        },
        [companyContexts, companyLabelsById, selectedCompanyIds],
    );

    const draftSnapshot = useMemo(
        () => snapshotFor(sortingListMode, sortingListVersion, sortingItems, allSortingColumns),
        [sortingListMode, sortingListVersion, sortingItems, allSortingColumns],
    );
    const isDirty = draftSnapshot !== savedSnapshot || Boolean(pendingDualWriteRows);
    const versionNumber = Number(String(sortingListVersion || '').trim());
    const hasValidVersionNumber =
        String(sortingListVersion || '').trim() && Number.isFinite(versionNumber);
    const incrementedVersion = hasValidVersionNumber ? versionNumber + 1 : '';
    const isDirtyForCancel = useMemo(() => {
        return Boolean(pendingDualWriteRows) || (
            snapshotForCancel(sortingListMode, sortingListVersion, sortingItems, allSortingColumns) !==
            snapshotForCancel(
                sortingListMode,
                savedSortingListVersion,
                savedSortingItems,
                allSortingColumns,
            )
        );
    }, [
        sortingListMode,
        sortingListVersion,
        sortingItems,
        savedSortingListVersion,
        savedSortingItems,
        allSortingColumns,
        pendingDualWriteRows,
    ]);
    const importSourceOptions = useMemo(
        () => pendingImportHeaders.map((header, index) => ({
            value: String(index),
            label: header || `Kolonne ${index + 1}`,
        })),
        [pendingImportHeaders],
    );
    const importTargetOptions = useMemo(
        () => sortingColumns.map((column) => ({
            value: column.field,
            label: column.label || column.field,
        })),
        [sortingColumns],
    );
    const visibleImportTargetFieldSet = useMemo(
        () => new Set(sortingColumns.map((column) => column.field)),
        [sortingColumns],
    );
    const visibleImportMappings = useMemo(
        () => importMappings.filter((mapping) => (
            !mapping.targetField || visibleImportTargetFieldSet.has(mapping.targetField)
        )),
        [importMappings, visibleImportTargetFieldSet],
    );
    const mappedImportColumns = useMemo(() => {
        const fields = new Set(importMappings.map((mapping) => mapping.targetField).filter(Boolean));
        return allSortingColumns.filter((column) => fields.has(column.field));
    }, [allSortingColumns, importMappings]);
    const positionalRowCount = selectedWasteReceiver?.sortingList?.list?.length || 0;
    // LEGACY_SORTING_ITEMS: The stored rows are keyed, so they only appear while the receiver's
    // columns still carry the ids (or names) they were written with.
    const legacyRowMapping = useMemo(
        () => summarizeLegacyRowMapping(
            selectedWasteReceiver?.sortingList?.sortingItems,
            selectedWasteReceiver?.columns,
        ),
        [selectedWasteReceiver],
    );
    const legacyRowCount = legacyRowMapping.total;
    const hasUnmappedLegacyRows = legacyRowMapping.unmapped > 0;
    // Saving legacy mode rewrites sorting_items from the rows on screen, so editing must stay
    // closed while rows are missing from it — a save would delete every unmapped row.
    const legacyEditingBlocked =
        sortingListMode === SORTING_LIST_MODES.LEGACY && hasUnmappedLegacyRows;

    const showMessage = useCallback((message, severity = 'success') => {
        setSnackbar({ message, severity });
    }, []);

    useEffect(() => {
        const missingCompanyIds = selectedCompanyIds.filter(
            (companyId) => companyId && !companyLabelsById[companyId],
        );
        if (missingCompanyIds.length === 0) return undefined;

        let cancelled = false;

        Promise.all(
            missingCompanyIds.map(async (companyId) => [
                companyId,
                await fetchCompanyLabel(companyId),
            ]),
        ).then((entries) => {
            if (cancelled) return;
            setCompanyLabelsById((currentLabels) => {
                const nextLabels = { ...currentLabels };
                entries.forEach(([companyId, label]) => {
                    nextLabels[companyId] = label || companyId;
                });
                return nextLabels;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [companyLabelsById, selectedCompanyIds]);

    const pendingDeleteRow = useMemo(() => {
        if (!pendingDeleteRowId) return null;
        return sortingItems.find((item) => item.id === pendingDeleteRowId) || null;
    }, [sortingItems, pendingDeleteRowId]);

    const pendingDeleteRowLabel = useMemo(() => {
        if (!pendingDeleteRow) return '';
        for (const column of sortingColumns) {
            const value = String(pendingDeleteRow[column.field] || '').trim();
            if (value) return value;
        }
        return String(
            pendingDeleteRow.constituent ||
            pendingDeleteRow.declaration ||
            pendingDeleteRow.unNumber ||
            ''
        ).trim();
    }, [pendingDeleteRow, sortingColumns]);

    useEffect(() => {
        if (!userData) return undefined;
        let cancelled = false;
        setLoadingCompanies(true);

        loadAccessibleDangerousWasteCompanies(userData)
            .then((loadedCompanies) => {
                if (cancelled) return;
                setCompanyContexts(loadedCompanies);
            })
            .catch((error) => {
                console.error('Error loading dangerous-waste companies:', error);
                if (!cancelled) showMessage('Kunne ikke hente virksomheder.', 'error');
            })
            .finally(() => {
                if (!cancelled) setLoadingCompanies(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userData, showMessage]);

    useEffect(() => {
        setLoadingWasteReceivers(true);

        const unsubscribe = onSnapshot(
            collection(db, WASTE_RECEIVERS_COLLECTION),
            (snap) => {
                const receivers = snap.docs
                    .map(normalizeTransitionWasteReceiverDoc)
                    .sort((a, b) => {
                        const nameCompare = (a.name || a.id).localeCompare(b.name || b.id);
                        return nameCompare || a.id.localeCompare(b.id);
                    });
                setWasteReceivers(receivers);
                setLoadingWasteReceivers(false);
            },
            (error) => {
                console.error('Error loading waste receivers:', error);
                showMessage('Kunne ikke hente affaldsmodtagere.', 'error');
                setLoadingWasteReceivers(false);
            },
        );

        return unsubscribe;
    }, [showMessage]);

    useEffect(() => {
        if (accessibleWasteReceivers.length === 0) {
            setSelectedWasteReceiverId('');
            return;
        }

        const requestedExists =
            requestedWasteReceiverId &&
            accessibleWasteReceivers.some((receiver) => receiver.id === requestedWasteReceiverId);
        const currentExists =
            selectedWasteReceiverId &&
            accessibleWasteReceivers.some((receiver) => receiver.id === selectedWasteReceiverId);
        const nextWasteReceiverId = requestedExists
            ? requestedWasteReceiverId
            : currentExists
              ? selectedWasteReceiverId
              : accessibleWasteReceivers[0].id;

        if (nextWasteReceiverId !== selectedWasteReceiverId) {
            setSelectedWasteReceiverId(nextWasteReceiverId);
        }
    }, [accessibleWasteReceivers, requestedWasteReceiverId, selectedWasteReceiverId]);

	useEffect(() => {
	    if (isEditing) return;

	    if (!selectedWasteReceiver) {
	        setSortingListMode(SORTING_LIST_MODES.POSITIONAL);
	        setSortingListVersion('');
	        setSavedSortingListVersion('');
	        setSortingItems([]);
	        setSavedSortingItems([]);
	        setSavedSnapshot(snapshotFor(SORTING_LIST_MODES.POSITIONAL, '', [], allSortingColumns));
	        setPendingDualWriteRows(null);
	        return;
	    }

        const versionText = String(selectedWasteReceiver.sortingList.version ?? 0);
        const nextMode = selectSortingListMode(selectedWasteReceiver.sortingList);
        const cleanSortingItems = nextMode === SORTING_LIST_MODES.LEGACY
            // LEGACY_SORTING_ITEMS: Adapt deprecated keyed rows to the shared table model.
            ? normalizeLegacyTableRows(
                legacyRowsToTableRows(
                    selectedWasteReceiver.sortingList.sortingItems,
                    selectedWasteReceiver.columns,
                ),
                allSortingColumns,
            )
            : positionalRowsToTableRows(
                selectedWasteReceiver.sortingList.list,
                selectedWasteReceiver.columns,
            );
        setSortingListMode(nextMode);
        setSortingListVersion(versionText);
        setSavedSortingListVersion(versionText);
        setSortingItems(cleanSortingItems);
        setSavedSortingItems(cleanSortingItems);
        setSavedSnapshot(snapshotFor(nextMode, versionText, cleanSortingItems, allSortingColumns));
        setPendingDualWriteRows(null);
    }, [selectedWasteReceiver, allSortingColumns, isEditing]);

    const getPersistedRowsForMode = (mode) => {
        if (!selectedWasteReceiver) return [];
        if (mode === SORTING_LIST_MODES.LEGACY) {
            // LEGACY_SORTING_ITEMS: Adapt the selected deprecated representation for inspection.
            return normalizeLegacyTableRows(
                legacyRowsToTableRows(
                    selectedWasteReceiver.sortingList.sortingItems,
                    selectedWasteReceiver.columns,
                ),
                allSortingColumns,
            );
        }
        return positionalRowsToTableRows(
            selectedWasteReceiver.sortingList.list,
            selectedWasteReceiver.columns,
        );
    };

    const activatePersistedMode = (mode) => {
        const rows = getPersistedRowsForMode(mode);
        const versionText = String(selectedWasteReceiver?.sortingList?.version ?? 0);
        setSortingListMode(mode);
        setSortingListVersion(versionText);
        setSavedSortingListVersion(versionText);
        setSortingItems(rows);
        setSavedSortingItems(rows);
        setSavedSnapshot(snapshotFor(mode, versionText, rows, allSortingColumns));
        setPendingDualWriteRows(null);
    };

    const requestSortingListMode = (mode) => {
        if (
            !userData?.isAdmin ||
            !selectedWasteReceiver ||
            mode === sortingListMode ||
            isEditing ||
            isDirty ||
            saving
        ) return;

        if (
            mode === SORTING_LIST_MODES.POSITIONAL &&
            positionalRowCount === 0 &&
            legacyRowCount > 0
        ) {
            setConversionDialogOpen(true);
            return;
        }

        activatePersistedMode(mode);
    };

    const closeConversionDialog = () => setConversionDialogOpen(false);

    const confirmLegacyConversion = () => {
        if (!selectedWasteReceiver) return;
        const convertedRows = convertLegacySortingItemsToPositionalRows(
            selectedWasteReceiver.sortingList.sortingItems,
            selectedWasteReceiver.columns,
        );
        const tableRows = positionalRowsToTableRows(convertedRows, selectedWasteReceiver.columns);
        const versionText = String(selectedWasteReceiver.sortingList.version ?? 0);
        setConversionDialogOpen(false);
        setSortingListMode(SORTING_LIST_MODES.POSITIONAL);
        setSortingListVersion(versionText);
        setSavedSortingListVersion(versionText);
        setSortingItems(tableRows);
        setSavedSortingItems([]);
        setSavedSnapshot(snapshotFor(
            SORTING_LIST_MODES.POSITIONAL,
            versionText,
            [],
            allSortingColumns,
        ));
        setPendingDualWriteRows(null);
        setIsEditing(true);
    };

    const updateSelectedWasteReceiver = (wasteReceiverId) => {
        setIsEditing(false);
        setPendingDualWriteRows(null);
        setConversionDialogOpen(false);
        setSelectedWasteReceiverId(wasteReceiverId);
        if (wasteReceiverId) {
            setSearchParams({ receiver: wasteReceiverId });
        } else {
            setSearchParams({});
        }
    };

    const updateSortingRow = (index, field, value) => {
        setPendingDualWriteRows(null);
        setSortingItems((items) =>
            items.map((item, itemIndex) =>
                itemIndex === index ? { ...item, [field]: value } : item,
            ),
        );
    };

    const addRow = () => {
        pendingScrollToNewRowRef.current = true;
        setPendingDualWriteRows(null);
        setSortingItems((items) => [...items, emptySortingRow(allSortingColumns)]);
    };

    const requestDeleteSortingRow = (rowId) => {
        if (!rowId) return;
        setPendingDeleteRowId(rowId);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setPendingDeleteRowId(null);
    };

    const confirmDeleteSortingRow = () => {
        const rowId = pendingDeleteRowId;
        if (!rowId) {
            closeDeleteDialog();
            return;
        }
        setPendingDualWriteRows(null);
        setSortingItems((items) => items.filter((item) => item.id !== rowId));
        closeDeleteDialog();
    };

	const importFile = async (event) => {
	    const file = event.target.files?.[0];
	    event.target.value = '';
	    if (!file) return;

	    try {
	        const text = await file.text();
	        const importDraft = createSortingListTsvImportDraft(text, allSortingColumns);
	        if (importDraft.headers.length === 0 || importDraft.rows.length === 0) {
	            showMessage('Filen indeholdt ingen datarækker.', 'warning');
	            return;
	        }

	        setPendingImportHeaders(importDraft.headers);
	        setPendingImportRows(importDraft.rows);
	        setImportMappings(importDraft.mappings);
	        setImportMappingDialogOpen(true);
	    } catch (error) {
	        console.error('Error importing sorting list file:', error);
	        showMessage('Kunne ikke importere filen.', 'error');
	    }
	};

	const downloadTsv = () => {
	    const receiverName = selectedWasteReceiver?.name || selectedWasteReceiverId;
	    const safeVersion = getSafeFileNamePart(sortingListVersion, '');
	    const versionPart = safeVersion ? `-v${safeVersion}` : '';
	    const filename = `sorteringsliste-${getSafeFileNamePart(receiverName)}${versionPart}.tsv`;
	    const tsv = serializeSortingListTsv(sortingItems, sortingColumns);
	    downloadTextFile(filename, `\uFEFF${tsv}\n`, 'text/tab-separated-values;charset=utf-8');
	    showMessage('Sorteringslisten er downloadet som TSV.');
	};

	const clearPendingImport = () => {
	    setPendingImportHeaders([]);
	    setPendingImportRows([]);
	    setImportMappings([]);
	    setPendingImportContext(null);
	    setPendingImportPositionalRows([]);
	    setImportDuplicateReviewRowNumbers([]);
	};

	const closeImportMappingDialog = () => {
	    setImportMappingDialogOpen(false);
	    clearPendingImport();
	};

	const updateImportMappingSource = (mappingId, sourceIndex) => {
	    const nextSourceIndex = Number(sourceIndex);
	    setImportMappings((mappings) =>
	        mappings.map((mapping) =>
	            mapping.id === mappingId
	                ? {
	                    ...mapping,
	                    sourceIndex: nextSourceIndex,
	                    sourceHeader: pendingImportHeaders[nextSourceIndex] || `Kolonne ${nextSourceIndex + 1}`,
	                }
	                : mapping,
	        ),
	    );
	};

	const updateImportMappingTarget = (mappingId, targetField) => {
	    setImportMappings((mappings) =>
	        mappings.map((mapping) =>
	            mapping.id === mappingId
	                ? { ...mapping, targetField }
	                : targetField && mapping.targetField === targetField
	                    ? { ...mapping, targetField: '' }
	                    : mapping,
	        ),
	    );
	};

	const confirmImportMapping = () => {
	    const columnValidation = validateTransitionColumns(selectedWasteReceiver?.columns || []);
	    if (!columnValidation.isValid) {
	        const conflictingNames = columnValidation.legacyKeyCollisionGroups
	            .flatMap((group) => group.map(({ column }) => column.display_name))
	            .join(', ');
	        showMessage(
	            conflictingNames
	                ? `Kolonnerne giver samme legacy-nøgle: ${conflictingNames}.`
	                : 'Kolonneopsætningen er ugyldig. Ret den under Setup før import.',
	            'warning',
	        );
	        return;
	    }

	    const mappedColumnIndexes = mappedImportColumns.map((column) => (
	        allSortingColumns.findIndex((candidate) => candidate.field === column.field)
	    ));
	    if (!mappedColumnIndexes.includes(0)) {
	        showMessage('Filens nøglekolonne skal matches til den første sorteringslistekolonne.', 'warning');
	        return;
	    }
	    // Starting a sorting list from scratch (no existing rows) requires every column to be
	    // mapped, so the list can never be created with fewer columns than the waste receiver
	    // has. An add/update import onto an already-populated list may still map only some
	    // columns and leave the rest untouched.
	    if (sortingItems.length === 0 && mappedColumnIndexes.length < allSortingColumns.length) {
	        showMessage(
	            'Ved oprettelse af en ny sorteringsliste skal alle kolonner mappes fra filen.',
	            'warning',
	        );
	        return;
	    }

	    const parsed = parseSortingListTsvRowsWithMappings(
	        pendingImportRows,
	        importMappings,
	        allSortingColumns,
	    );

	    if (parsed.length === 0) {
	        showMessage('Kolonnematchingen gav ingen sorteringsrækker.', 'warning');
	        return;
	    }

	    const importedRows = tableRowsToPositionalList(parsed, selectedWasteReceiver.columns);
	    const existingRows = tableRowsToPositionalList(sortingItems, selectedWasteReceiver.columns);
	    const resolution = resolveSortingListAddOrUpdateImport({
	        existingRows,
	        importedRows,
	        mappedColumnIndexes,
	        columnCount: allSortingColumns.length,
	    });

	    if (resolution.blankImportedFirstValueRows.length > 0) {
	        showMessage(
	            `Første kolonne er påkrævet i de importerede rækker ${resolution.blankImportedFirstValueRows.join(', ')}.`,
	            'warning',
	        );
	        return;
	    }
	    if (resolution.blankFirstValueRows.length > 0) {
	        showMessage(
	            `Første kolonne er påkrævet i række ${resolution.blankFirstValueRows.join(', ')}.`,
	            'warning',
	        );
	        return;
	    }
	    if (resolution.duplicateFirstValueRowGroups.length > 0) {
	        const duplicateRows = resolution.duplicateFirstValueRowGroups.flat()
	            .sort((left, right) => left - right);
	        showMessage(
	            `Første kolonne skal være unik. Konflikt i rækkerne ${duplicateRows.join(', ')}.`,
	            'warning',
	        );
	        return;
	    }

	    setImportMappingDialogOpen(false);
	    setPendingImportPositionalRows(importedRows);
	    setPendingImportContext({
	        mappedColumnIndexes,
	        mappedColumnNames: mappedImportColumns.map((column) => column.label || column.field),
	    });
	    // Freeze which uploaded rows are under review so an edit that resolves a collision
	    // updates the counts without the row vanishing from under the cursor.
	    setImportDuplicateReviewRowNumbers(
	        resolution.consolidatedDuplicates
	            .flatMap((duplicate) => duplicate.sourceRowNumbers)
	            .sort((left, right) => left - right),
	    );
	    setImportDialogOpen(true);
	};

	// The import preview resolves live: every keystroke in the duplicate review re-runs the
	// add-or-update resolution, so counts and per-row status reflect the edited upload.
	const pendingImportResolution = useMemo(() => {
	    if (!pendingImportContext || !selectedWasteReceiver) return null;
	    const existingRows = tableRowsToPositionalList(sortingItems, selectedWasteReceiver.columns);
	    const resolution = resolveSortingListAddOrUpdateImport({
	        existingRows,
	        importedRows: pendingImportPositionalRows,
	        mappedColumnIndexes: pendingImportContext.mappedColumnIndexes,
	        columnCount: allSortingColumns.length,
	    });
	    return { ...resolution, mappedColumnNames: pendingImportContext.mappedColumnNames };
	}, [
	    pendingImportContext,
	    pendingImportPositionalRows,
	    sortingItems,
	    selectedWasteReceiver,
	    allSortingColumns,
	]);

	const importDuplicateStatusByRowNumber = useMemo(() => {
	    const statuses = new Map();
	    (pendingImportResolution?.consolidatedDuplicates || []).forEach((duplicate) => {
	        duplicate.sourceRowNumbers.forEach((rowNumber) => {
	            statuses.set(
	                rowNumber,
	                rowNumber === duplicate.usedRowNumber ? 'used' : 'overwritten',
	            );
	        });
	    });
	    return statuses;
	}, [pendingImportResolution]);

	const importBlockingIssue = useMemo(() => {
	    if (!pendingImportResolution) return '';
	    const blankRows = [
	        ...pendingImportResolution.blankImportedFirstValueRows,
	        ...pendingImportResolution.blankFirstValueRows,
	    ];
	    if (blankRows.length > 0) {
	        return `Første kolonne er påkrævet i rækkerne ${[...new Set(blankRows)].sort((a, b) => a - b).join(', ')}.`;
	    }
	    if (pendingImportResolution.duplicateFirstValueRowGroups.length > 0) {
	        const rows = pendingImportResolution.duplicateFirstValueRowGroups.flat()
	            .sort((left, right) => left - right);
	        return `Første kolonne skal være unik. Konflikt i rækkerne ${rows.join(', ')}.`;
	    }
	    return '';
	}, [pendingImportResolution]);

	const updateImportedRowValue = (rowNumber, columnIndex, value) => {
	    setPendingImportPositionalRows((rows) => rows.map((row, index) => (
	        index === rowNumber - 1
	            ? {
	                ...row,
	                values: row.values.map((current, valueIndex) => (
	                    valueIndex === columnIndex ? value : current
	                )),
	            }
	            : row
	    )));
	};

	const closeImportDialog = () => {
	    setImportDialogOpen(false);
	    clearPendingImport();
	};

	const applyImportAddOrUpdate = () => {
	    const resolution = pendingImportResolution;
	    if (!resolution || !selectedWasteReceiver) return;
	    if (importBlockingIssue) {
	        showMessage(importBlockingIssue, 'warning');
	        return;
	    }
	    closeImportDialog();
	    setSortingItems(positionalRowsToTableRows(
	        resolution.rows,
	        selectedWasteReceiver.columns,
	    ));
	    setPendingDualWriteRows(resolution.rows);
	    const duplicateSummary = resolution.duplicatesConsolidated > 0
	        ? ` ${resolution.duplicatesConsolidated} dubletter blev samlet.`
	        : '';
	    showMessage(
	        `${resolution.updatedCount} rækker opdateret, ${resolution.addedCount} rækker tilføjet.${duplicateSummary}`,
	    );
	};

	const saveConfig = async (versionToSave = sortingListVersion) => {
	    if (!selectedWasteReceiver) return;
        const nextVersionNumber = Number(String(versionToSave || '').trim());
        if (!String(versionToSave || '').trim() || !Number.isFinite(nextVersionNumber)) {
            showMessage('Version skal være et tal.', 'warning');
            return;
        }

        // LEGACY_SORTING_ITEMS: Never write the on-screen rows back when the stored list holds
        // rows the current columns cannot address — the write replaces sorting_items wholesale.
        if (legacyEditingBlocked) {
            showMessage(
                `${legacyRowMapping.unmapped} af ${legacyRowMapping.total} legacy-rækker passer `
                + 'ikke til kolonnerne og kan ikke gemmes uden at blive slettet.',
                'warning',
            );
            return;
        }

        const columnValidation = validateTransitionColumns(selectedWasteReceiver.columns);
        if (!columnValidation.isValid) {
            const conflictingNames = columnValidation.legacyKeyCollisionGroups
                .flatMap((group) => group.map(({ column }) => column.display_name))
                .join(', ');
            showMessage(
                conflictingNames
                    ? `Kolonnerne giver samme legacy-nøgle: ${conflictingNames}.`
                    : 'Kolonneopsætningen er ugyldig. Ret den under Setup før listen gemmes.',
                'warning',
            );
            return;
        }

        let positionalValidation = null;
        if (sortingListMode === SORTING_LIST_MODES.POSITIONAL) {
            positionalValidation = validatePositionalTableRows(
                sortingItems,
                selectedWasteReceiver.columns,
            );
            if (positionalValidation.blankFirstValueRows.length > 0) {
                showMessage(
                    `Første kolonne er påkrævet i række ${positionalValidation.blankFirstValueRows.join(', ')}.`,
                    'warning',
                );
                return;
            }
            if (positionalValidation.duplicateFirstValueRowGroups.length > 0) {
                const duplicateRows = positionalValidation.duplicateFirstValueRowGroups
                    .flat()
                    .sort((left, right) => left - right);
                showMessage(
                    `Første kolonne skal være unik. Konflikt i rækkerne ${duplicateRows.join(', ')}.`,
                    'warning',
                );
                return;
            }
        }

	    setSaving(true);

        const cleanSortingItems = sortingListMode === SORTING_LIST_MODES.POSITIONAL
            ? positionalRowsToTableRows(
                positionalValidation.rows,
                selectedWasteReceiver.columns,
            )
            // LEGACY_SORTING_ITEMS: Retain the established legacy sanitization behavior.
            : normalizeLegacyTableRows(sortingItems, allSortingColumns);
        const lastUpdate = Date.now();
        const payload = pendingDualWriteRows
            ? buildDualFormatSortingListImportPayload({
                existingSortingList: selectedWasteReceiver.sortingList,
                rows: pendingDualWriteRows,
                columns: selectedWasteReceiver.columns,
                version: nextVersionNumber,
                lastUpdate,
            })
            : buildSortingListWritePayload({
                existingSortingList: selectedWasteReceiver.sortingList,
                mode: sortingListMode,
                rows: cleanSortingItems,
                columns: selectedWasteReceiver.columns,
                version: nextVersionNumber,
                lastUpdate,
            });

	        try {
	            await setDoc(
	                doc(db, WASTE_RECEIVERS_COLLECTION, selectedWasteReceiver.id),
	                payload,
	                { merge: true },
	            );
	            const savedVersionText = String(nextVersionNumber);
	            setSortingListVersion(savedVersionText);
	            setSavedSortingListVersion(savedVersionText);
	            setSortingItems(cleanSortingItems);
	            setSavedSortingItems(cleanSortingItems);
	            setPendingDualWriteRows(null);
	            setSavedSnapshot(snapshotFor(
                    sortingListMode,
                    savedVersionText,
                    cleanSortingItems,
                    allSortingColumns,
                ));
	            setIsEditing(false);
	            showMessage('Sorteringslisten er gemt.');
        } catch (error) {
            console.error('Error saving dangerous-waste sorting list:', error);
            showMessage(
                `Kunne ikke gemme sorteringslisten: ${describeFirestoreError(error)}`,
                'error',
            );
	        } finally {
	            setSaving(false);
	            setVersionDialogOpen(false);
	            setConfirmSaveDialogOpen(false);
	        }
	    };

	    const handleSavePressed = () => {
	        if (!isDirty || saving || !selectedWasteReceiver) return;
	        if (!hasValidVersionNumber) {
	            showMessage('Version skal være et tal.', 'warning');
	            return;
	        }
	        if (String(sortingListVersion || '').trim() === String(savedSortingListVersion || '').trim()) {
	            setVersionDialogOpen(true);
	            return;
	        }
	        setConfirmSaveDialogOpen(true);
	    };

	    const closeVersionDialog = () => setVersionDialogOpen(false);

	    const closeConfirmSaveDialog = () => setConfirmSaveDialogOpen(false);

	    const confirmManualSave = () => {
	        closeConfirmSaveDialog();
	        saveConfig();
	    };

	    const saveCurrentVersion = () => {
	        closeVersionDialog();
	        saveConfig(sortingListVersion);
	    };

	    const saveIncrementedVersion = () => {
	        closeVersionDialog();
	        saveConfig(incrementedVersion);
	    };

	const handlePrimaryActionPressed = () => {
	    if (saving || !selectedWasteReceiver || legacyEditingBlocked) return;

        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        if (!isDirty) {
            setIsEditing(false);
            return;
        }

	    handleSavePressed();
	};

	const restoreSavedDraftAndExit = () => {
	    setSortingListVersion(savedSortingListVersion);
	    setSortingItems(savedSortingItems);
	    setPendingDualWriteRows(null);
	    setIsEditing(false);
	};

	const handleCancelEditPressed = () => {
	    if (saving || !isEditing) return;
	    if (isDirtyForCancel) {
	        setCancelEditDialogOpen(true);
	        return;
	    }
	    restoreSavedDraftAndExit();
	};

	const closeCancelEditDialog = () => setCancelEditDialogOpen(false);

	const confirmCancelEdit = () => {
	    closeCancelEditDialog();
	    restoreSavedDraftAndExit();
	};

    useEffect(() => {
        if (!pendingScrollToNewRowRef.current) return;
        pendingScrollToNewRowRef.current = false;

        const el = sortingTableWrapRef.current;
        if (!el) return;

        // Let the new row render and layout before scrolling.
        window.requestAnimationFrame(() => {
            try {
                el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            } catch {
                el.scrollTop = el.scrollHeight;
            }
        });
    }, [sortingItems.length]);

    const renderEditableTable = () => {
        const columns = sortingColumns;
        const rows = sortingItems;
        const updateRow = updateSortingRow;
        const gridTemplateColumns = `52px ${columns.map((column) => column.width).join(' ')} 52px`;

        return (
            <div className="sorting-list-table-wrap" ref={sortingTableWrapRef}>
                <div className="sorting-list-grid header" style={{ gridTemplateColumns }}>
                    <div>#</div>
                    {columns.map((column) => (
                        <div
                            key={column.field}
                            className={column.isHidden ? 'hidden-sorting-column' : undefined}
                        >
                            {t(column.label)}
                        </div>
                    ))}
                    <div />
                </div>

                <div className="sorting-list-table-body">
                    {rows.map((row, index) => (
                        <div
                            className="sorting-list-grid row"
                            style={{ gridTemplateColumns }}
	                            key={row.id || `sorting-${index}`}
	                        >
	                            <div className="row-number">{index + 1}</div>
	                            {columns.map((column) => (
	                                <div
	                                    key={column.field}
	                                    className={`table-input-cell${column.isHidden ? ' hidden-sorting-column' : ''}`}
	                                >
	                                    <AutoSizeTextarea
	                                        className={`table-input${column.isHidden ? ' hidden-sorting-column' : ''}`}
	                                        value={row[column.field] || ''}
	                                        rows={1}
	                                        required={
	                                            sortingListMode === SORTING_LIST_MODES.POSITIONAL &&
	                                            column.columnIndex === 0
	                                        }
	                                        aria-label={`${column.label}, række ${index + 1}`}
	                                        onChange={(event) =>
	                                            updateRow(index, column.field, event.target.value)
	                                        }
	                                    />
	                                </div>
	                            ))}
	                            <Tooltip title="Slet række">
	                                <button
	                                    type="button"
                                    className="icon-table-button"
                                    onClick={() => requestDeleteSortingRow(row.id)}
                                    aria-label="Slet række"
                                >
                                    <DeleteIcon fontSize="small" />
                                </button>
                            </Tooltip>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderReadOnlyTable = () => {
        const columns = sortingColumns;
        const rows = sortingItems;
        const gridTemplateColumns = `52px ${columns.map((column) => column.width).join(' ')}`;

        return (
            <div className="sorting-list-table-wrap" ref={sortingTableWrapRef}>
                <div className="sorting-list-grid header" style={{ gridTemplateColumns }}>
                    <div>#</div>
                    {columns.map((column) => (
                        <div
                            key={column.field}
                            className={column.isHidden ? 'hidden-sorting-column' : undefined}
                        >
                            {t(column.label)}
                        </div>
                    ))}
                </div>

                <div className="sorting-list-table-body">
                    {rows.map((row, index) => (
                        <div
                            className="sorting-list-grid row"
                            style={{ gridTemplateColumns }}
                            key={row.id || `sorting-${index}`}
                        >
                            <div className="row-number">{index + 1}</div>
                            {columns.map((column) => (
                                <div
                                    key={column.field}
                                    className={`table-cell${column.isHidden ? ' hidden-sorting-column' : ''}`}
                                >
                                    {row[column.field] || ''}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="dangerous-waste-sorting-list-page">
            {(loadingCompanies || loadingWasteReceivers || saving) && <LinearProgress className="top-progress" />}

	            <div className="sorting-list-toolbar">
	                <div className="toolbar-title">
	                    <h1>Sorteringsliste</h1>
	                    <div className="toolbar-meta">
                        <Chip
                            size="small"
                            label={`${sortingItems.length} sorteringsrækker`}
                            className="meta-chip"
                        />
                        <Chip
                            size="small"
                            label={sortingListMode === SORTING_LIST_MODES.POSITIONAL
                                ? 'Ny liste'
                                : 'Legacy sorting_items'}
                            className="meta-chip"
                        />
                    </div>
	                </div>

	                <div className="toolbar-actions">
                        {isEditing && canEditSelectedSortingList && hasSortingColumns && (
	                        <>
                            <input
                                ref={sortingFileInputRef}
                                type="file"
                                accept=".tsv,.csv,text/tab-separated-values,text/csv,text/plain"
                                hidden
                                onChange={importFile}
                            />
                            <Tooltip title="Importer sorteringsliste fra CSV eller TSV">
                                <Button
                                    variant="outlined"
                                    startIcon={<UploadFileIcon />}
                                    onClick={() => sortingFileInputRef.current?.click()}
                                >
                                    Importer csv/tsv
                                </Button>
	                            </Tooltip>
	                        </>
	                    )}
                        {isEditing && canEditSelectedSortingList && hasSortingColumns && (
	                        <Tooltip title="Download sorteringsliste som TSV">
	                            <Button
	                                variant="outlined"
	                                startIcon={<DownloadIcon />}
	                                onClick={downloadTsv}
	                                disabled={saving || !selectedWasteReceiver || !hasSortingColumns}
	                            >
	                                Download tsv
	                            </Button>
	                        </Tooltip>
	                    )}
	                    {isEditing && (
	                        <Button
	                            variant="outlined"
	                            onClick={handleCancelEditPressed}
	                            disabled={saving}
	                        >
	                            Annuller
	                        </Button>
	                    )}
                        {canEditSelectedSortingList && (
                            <Button
                                variant="contained"
                                startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                                disabled={
                                    saving
                                    || !selectedWasteReceiver
                                    || !hasSortingColumns
                                    || legacyEditingBlocked
                                }
                                onClick={handlePrimaryActionPressed}
                        >
                            {isEditing ? 'Gem' : 'Rediger'}
                        </Button>
                        )}
		                </div>
		            </div>

            {userData?.isAdmin && selectedWasteReceiver && (
                <div className="sorting-list-format-toggle" aria-label="Listeformat">
                    <Button
                        variant={sortingListMode === SORTING_LIST_MODES.POSITIONAL ? 'contained' : 'outlined'}
                        onClick={() => requestSortingListMode(SORTING_LIST_MODES.POSITIONAL)}
                        disabled={saving || isEditing || isDirty}
                        aria-pressed={sortingListMode === SORTING_LIST_MODES.POSITIONAL}
                    >
                        Ny liste ({positionalRowCount})
                    </Button>
                    <Button
                        variant={sortingListMode === SORTING_LIST_MODES.LEGACY ? 'contained' : 'outlined'}
                        onClick={() => requestSortingListMode(SORTING_LIST_MODES.LEGACY)}
                        disabled={saving || isEditing || isDirty}
                        aria-pressed={sortingListMode === SORTING_LIST_MODES.LEGACY}
                    >
                        Legacy sorting_items ({hasUnmappedLegacyRows
                            ? `${legacyRowMapping.mapped}/${legacyRowCount}`
                            : legacyRowCount})
                        <span className="legacy-format-badge">Legacy</span>
                    </Button>
                </div>
            )}

            <div className={`sorting-list-controls${isEditing ? ' editing' : ''}`}>
                <TextField
                    select
                    label="Affaldsmodtager"
                    value={selectedWasteReceiverId}
                    disabled={loadingWasteReceivers || accessibleWasteReceivers.length === 0}
                    onChange={(event) => {
                        if (!isEditing) updateSelectedWasteReceiver(event.target.value);
                    }}
                    className={`machine-select${isEditing ? ' machine-select-locked' : ''}`}
                    size="small"
                    SelectProps={{
                        readOnly: isEditing,
                        MenuProps: {
                            PaperProps: { className: 'sorting-list-select-menu' },
                        },
                    }}
                >
                    {accessibleWasteReceivers.map((receiver) => (
                        <MenuItem key={receiver.id} value={receiver.id}>
                            {receiver.name || receiver.id}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Version"
                    value={sortingListVersion}
                    onChange={(event) => setSortingListVersion(event.target.value)}
                    className="version-field"
                    size="small"
                    disabled={!selectedWasteReceiver}
                    inputProps={{ readOnly: !isEditing }}
                />

                <div className="selected-machine-meta">
                    {selectedWasteReceiver && (
                        <>
                            <span>{selectedWasteReceiver.companies.length} virksomheder</span>
                            <span>{sortingColumns.length} kolonner</span>
                            <span>
                                {selectedCompanyLabels.length > 0
                                    ? selectedCompanyLabels.join(', ')
                                    : 'Ingen virksomheder tilknyttet'}
                            </span>
                        </>
                    )}
                </div>

                {isEditing && canEditSelectedSortingList && (
                    <div className="sorting-list-controls-right">
                        <Button
	                            variant="outlined"
	                            startIcon={<AddIcon />}
	                            onClick={addRow}
	                            disabled={saving || !selectedWasteReceiver || !hasSortingColumns}
	                            size="small"
                        >
                            Tilføj affaldstype
                        </Button>
                    </div>
                )}
            </div>

            {accessibleWasteReceivers.length === 0 && !loadingWasteReceivers ? (
                <Alert severity="info">
                    Du har ingen tilgængelige affaldsmodtagere.
                </Alert>
            ) : !selectedWasteReceiver ? (
                <Alert severity="info">
                    Vælg en affaldsmodtager for at redigere sorteringslisten.
                </Alert>
            ) : !hasSortingColumns ? (
                null
            ) : (
                <>
                    {/* LEGACY_SORTING_ITEMS: Keyed rows go blank when the columns they were
                        written against change, so say so instead of showing an empty list. */}
                    {sortingListMode === SORTING_LIST_MODES.LEGACY && hasUnmappedLegacyRows && (
                        <Alert severity="warning" className="legacy-mapping-alert">
                            {legacyRowMapping.unmapped} af {legacyRowMapping.total} rækker i{' '}
                            <strong>sorting_items</strong> passer ikke til kolonnerne på denne
                            affaldsmodtager og vises derfor ikke.
                            {legacyRowMapping.unmatchedKeys.length > 0 && (
                                <> Nøglerne{' '}
                                    <strong>{legacyRowMapping.unmatchedKeys.join(', ')}</strong>{' '}
                                    findes hverken som kolonne-id eller kolonnenavn.
                                </>
                            )}
                            {' '}Redigering er slået fra i legacy-tilstand, indtil de stemmer
                            overens — et gem ville slette rækkerne. Ret kolonne-id'erne under
                            Setup, eller brug <strong>Ny liste</strong>.
                        </Alert>
                    )}
                    {isEditing && canEditSelectedSortingList ? renderEditableTable() : renderReadOnlyTable()}
                </>
            )}

            <Dialog
                className="sorting-list-dialog"
                open={conversionDialogOpen}
                onClose={closeConversionDialog}
            >
                <DialogTitle>Konvertér legacy-listen?</DialogTitle>
                <DialogContent>
                    Der findes {legacyRowCount} rækker i <strong>Legacy sorting_items</strong>, men
                    ingen rækker i den nye liste. Vil du kopiere legacy-rækkerne til kolonnernes
                    nuværende rækkefølge? Konverteringen gemmes først, når du klikker Gem.
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConversionDialog}>Annuller</Button>
                    <Button variant="contained" onClick={confirmLegacyConversion}>
                        Konvertér til ny liste
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog className="sorting-list-dialog" open={versionDialogOpen} onClose={closeVersionDialog}>
	                <DialogTitle>Versionsnummer</DialogTitle>
	                <DialogContent>
	                    Vil du forøge versionsnummeret fra {String(sortingListVersion || '').trim()} til{' '}
	                    {incrementedVersion}?
	                </DialogContent>
	                <DialogActions>
	                    <Button onClick={closeVersionDialog}>Annuller</Button>
	                    <Button onClick={saveCurrentVersion} disabled={saving}>
	                        Gem som {String(sortingListVersion || '').trim()}
	                    </Button>
	                    <Button variant="contained" onClick={saveIncrementedVersion} disabled={saving}>
	                        Gem som {incrementedVersion}
	                    </Button>
	                </DialogActions>
	            </Dialog>

	            <Dialog className="sorting-list-dialog" open={confirmSaveDialogOpen} onClose={closeConfirmSaveDialog}>
	                <DialogTitle>Gem ændringer?</DialogTitle>
	                <DialogContent>Vil du gemme dine ændringer?</DialogContent>
	                <DialogActions>
	                    <Button onClick={closeConfirmSaveDialog}>Annuller</Button>
	                    <Button variant="contained" onClick={confirmManualSave} disabled={saving}>
	                        Gem
	                    </Button>
	                </DialogActions>
	            </Dialog>

				<Dialog className="sorting-list-dialog" open={deleteDialogOpen} onClose={closeDeleteDialog}>
					<DialogTitle>Slet række?</DialogTitle>
					<DialogContent>
	                    {pendingDeleteRowLabel ? (
	                        <div style={{ marginBottom: 10 }}>
                            <strong>{pendingDeleteRowLabel}</strong>
                        </div>
                    ) : null}
                    Rækken fjernes kun fra dit udkast og bliver først fjernet fra sorteringslisten,
                    når du klikker Gem.
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog}>Annuller</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDeleteSortingRow}
                        disabled={saving}
                    >
                        Slet række
                    </Button>
				</DialogActions>
			</Dialog>

			<Dialog
				className="sorting-list-dialog sorting-list-import-mapping-dialog"
				open={importMappingDialogOpen}
				onClose={closeImportMappingDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Match filkolonner</DialogTitle>
				<DialogContent>
					<div className="import-mapping-intro">
						Kontrollér kolonnematchingen før import.
					</div>
					<div className="import-mapping-preview">
						<strong>Kolonner der opdateres:</strong>{' '}
						{mappedImportColumns.length > 0
							? mappedImportColumns.map((column) => column.label || column.field).join(', ')
							: 'Ingen'}
					</div>
					<div className="import-mapping-grid import-mapping-grid-header">
						<div>Filkolonne</div>
						<div>Sorteringslistekolonne</div>
					</div>
					<div className="import-mapping-list">
						{visibleImportMappings.map((mapping) => (
							<div className="import-mapping-grid" key={mapping.id}>
									<TextField
										select
										value={String(mapping.sourceIndex)}
										onChange={(event) =>
											updateImportMappingSource(mapping.id, event.target.value)
										}
									size="small"
									SelectProps={{
										renderValue: (selected) => {
											return importSourceOptions.find((option) => option.value === String(selected))?.label || selected;
										},
										MenuProps: {
											PaperProps: { className: 'sorting-list-select-menu' },
										},
									}}
								>
									{importSourceOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
									<TextField
										select
										value={mapping.targetField || ''}
										onChange={(event) =>
											updateImportMappingTarget(mapping.id, event.target.value)
										}
									size="small"
									SelectProps={{
										displayEmpty: true,
										renderValue: (selected) => {
											if (!selected) {
												return <span className="ignore-select-value">Ignorer</span>;
											}
											return importTargetOptions.find((option) => option.value === selected)?.label || selected;
										},
										MenuProps: {
											PaperProps: { className: 'sorting-list-select-menu' },
										},
									}}
								>
									<MenuItem className="ignore-menu-item" value="">
										Ignorer
									</MenuItem>
									{importTargetOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							</div>
						))}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeImportMappingDialog}>Annuller</Button>
					<Button
						variant="contained"
						onClick={confirmImportMapping}
						disabled={saving || importMappings.length === 0}
					>
						Fortsæt
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				className="sorting-list-dialog sorting-list-import-preview-dialog"
				open={importDialogOpen}
				onClose={closeImportDialog}
				fullWidth
				maxWidth={false}
			>
				<DialogTitle>Tilføj eller opdater sorteringsliste</DialogTitle>
				<DialogContent>
					<p>
						Følgende kolonner opdateres:{' '}
						<strong>{pendingImportResolution?.mappedColumnNames?.join(', ') || 'Ingen'}</strong>.
					</p>
					<p>
						{pendingImportResolution?.updatedCount || 0} eksisterende rækker opdateres, og{' '}
						{pendingImportResolution?.addedCount || 0} nye rækker tilføjes.
					</p>
					{pendingImportResolution?.duplicatesConsolidated > 0 && (
						<p>
							{pendingImportResolution.duplicatesConsolidated} dubletter i filen blev samlet;
							 den sidste forekomst bruges.
						</p>
					)}
					{importDuplicateReviewRowNumbers.length > 0 && (
						<>
							<p>
								Ret rækkerne herunder, hvis du hellere vil beholde dem hver for sig.
								Gør du den første kolonne unik, bliver rækken bevaret i stedet for
								at blive overskrevet.
							</p>
							<div className="import-duplicate-table">
								<table>
									<thead>
										<tr>
											<th scope="col">Række</th>
											{pendingImportContext?.mappedColumnIndexes.map((columnIndex) => (
												<th key={`duplicate-header-${columnIndex}`} scope="col">
													{allSortingColumns[columnIndex]?.label
														|| `Kolonne ${columnIndex + 1}`}
												</th>
											))}
											<th scope="col">Status</th>
										</tr>
									</thead>
									<tbody>
										{importDuplicateReviewRowNumbers.map((rowNumber) => {
											const status = importDuplicateStatusByRowNumber.get(rowNumber);
											return (
												<tr
													key={`duplicate-row-${rowNumber}`}
													className={`import-duplicate-row is-${status || 'unique'}`}
												>
													<th scope="row">{rowNumber}</th>
													{pendingImportContext?.mappedColumnIndexes.map((columnIndex) => (
														<td key={`duplicate-cell-${rowNumber}-${columnIndex}`}>
															{/* A textarea rather than an input: imported declarations run long,
																and the row has to be readable in full before it is judged. */}
															<AutoSizeTextarea
																minHeightPx={IMPORT_DUPLICATE_INPUT_MIN_HEIGHT_PX}
																rows={1}
																aria-label={`${allSortingColumns[columnIndex]?.label || `Kolonne ${columnIndex + 1}`}, importrække ${rowNumber}`}
																value={pendingImportPositionalRows[rowNumber - 1]?.values[columnIndex] ?? ''}
																onChange={(event) => updateImportedRowValue(
																	rowNumber,
																	columnIndex,
																	event.target.value,
																)}
															/>
														</td>
													))}
													<td className="import-duplicate-status">
														{status === 'used' && 'Bruges'}
														{status === 'overwritten' && 'Overskrives'}
														{!status && 'Bevares'}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</>
					)}
					{importBlockingIssue && (
						<Alert severity="warning" className="import-duplicate-alert">
							{importBlockingIssue}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeImportDialog}>Annuller</Button>
					<Button
						variant="contained"
						onClick={applyImportAddOrUpdate}
						disabled={saving || Boolean(importBlockingIssue)}
					>
						Tilføj/opdater
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog className="sorting-list-dialog" open={cancelEditDialogOpen} onClose={closeCancelEditDialog}>
				<DialogTitle>Annuller ændringer?</DialogTitle>
				<DialogContent>
					Du har ændringer, der ikke er gemt. Hvis du annullerer nu, bliver ændringerne
	                    kasseret.
	                </DialogContent>
	                <DialogActions>
	                    <Button onClick={closeCancelEditDialog}>Fortsæt redigering</Button>
	                    <Button
	                        variant="contained"
	                        color="error"
	                        onClick={confirmCancelEdit}
	                        disabled={saving}
	                    >
	                        Annuller ændringer
	                    </Button>
	                </DialogActions>
	            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={3500}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar && (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
                        {snackbar.message}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};

export default DangerousWasteSortingListPage;
